// ============================================================
// In-app messages between an owner and the management team.
//
// One thread per customer. The owner sees a single conversation; the admin
// sees an inbox of all of them. Both talk to the same endpoints
// (Backend/pmMessaging.js), mounted under the PM API — so this file is the
// only place in the frontend that knows their shape.
//
// There is no socket: the backend is a stateless Cloud Run service that
// scales to zero, and a websocket per signed-in owner is a much bigger thing
// to run than a poll every few seconds while a thread is actually open. See
// useThreadPoll below for where that polling is bounded.
// ============================================================

import { portalFetch } from './api';

export type MessageFrom = 'owner' | 'tenant' | 'admin';

/** What a customer chose to talk about; it decides which team member sees the thread. */
export type ChatTopic = 'realestate' | 'pm';

export interface PmMessage {
  id: string;
  threadId: string;
  body: string;
  from: MessageFrom;
  authorName: string;
  createdAt: string;
}

export interface PmThread {
  id: string;
  kind: 'owner' | 'tenant';
  participantId: string;
  participantName: string;
  participantEmail: string;
  status: 'open' | 'archived';
  topic?: ChatTopic | null;
  /** Admin side only: who the thread is routed to ([] = every admin). */
  assignedTo?: string[];
  lastMessage: { text: string; at: string; from: MessageFrom; authorName: string } | null;
  unreadForAdmin: number;
  unreadForParticipant: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PmRecipient {
  id: string;
  kind: 'owner' | 'tenant';
  name: string;
  email: string;
  pmActive: boolean;
  threadId: string | null;
  unread: number;
}

// ---------- the customer side, for both kinds of customer ----------
//
// An owner and a tenant have the same conversation with the same team, so
// they run the same four calls against the same router — only the mount
// point and the credential differ. The owner rides the ordinary app session
// (portalFetch attaches it); the tenant has a token of its own, minted at
// OTP login, because the rest of the tenant API is addressed by id with no
// auth at all and private correspondence cannot sit behind that.

// The tenant token beats the owner one that portalFetch would otherwise
// attach: headers passed in options are merged last.
function tenantAuth(): Record<string, string> {
  try {
    const t = localStorage.getItem(TENANT_TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

export const TENANT_TOKEN_KEY = 'pmTenantToken';

export interface MessageChannel {
  fetchThread: () => Promise<{ thread: PmThread | null; messages: PmMessage[]; isAdmin?: boolean }>;
  send: (body: string, topic?: ChatTopic | null) => Promise<{ thread: PmThread; message: PmMessage }>;
  setTopic: (topic: ChatTopic) => Promise<{ thread: PmThread }>;
  markRead: () => Promise<{ ok: boolean }>;
  summary: () => Promise<{ role: MessageFrom; unread: number; threads: number }>;
}

function makeChannel(base: string, auth: () => Record<string, string>): MessageChannel {
  return {
    fetchThread: () => portalFetch(`${base}/messages/me`, { headers: auth() }),
    send: (body: string, topic?: ChatTopic | null) => portalFetch(`${base}/messages/me`, {
      method: 'POST', headers: auth(), body: JSON.stringify(topic ? { body, topic } : { body }),
    }),
    setTopic: (topic: ChatTopic) => portalFetch(`${base}/messages/me/topic`, {
      method: 'POST', headers: auth(), body: JSON.stringify({ topic }),
    }),
    markRead: () => portalFetch(`${base}/messages/me/read`, { method: 'POST', headers: auth() }),
    summary: () => portalFetch(`${base}/messages/summary`, { headers: auth() }),
  };
}

export const ownerChannel: MessageChannel = makeChannel('/pm', () => ({}));
export const tenantChannel: MessageChannel = makeChannel('/tenant', tenantAuth);

// ---------- badge (admin + owner) ----------

export const fetchMessageSummary = () =>
  portalFetch<{ role: MessageFrom; unread: number; threads: number }>('/pm/messages/summary');

// ---------- admin side ----------

export const fetchThreads = (status: 'open' | 'archived' | 'all' = 'open', q = '') =>
  portalFetch<{ threads: PmThread[]; unread: number }>(
    `/pm/messages/threads?status=${encodeURIComponent(status)}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
  );

export const fetchThread = (id: string) =>
  portalFetch<{ thread: PmThread; messages: PmMessage[] }>(`/pm/messages/threads/${encodeURIComponent(id)}`);

export const replyToThread = (id: string, body: string) =>
  portalFetch<{ thread: PmThread; message: PmMessage }>(`/pm/messages/threads/${encodeURIComponent(id)}`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });

export const markThreadRead = (id: string) =>
  portalFetch<{ ok: boolean }>(`/pm/messages/threads/${encodeURIComponent(id)}/read`, { method: 'POST' });

export const setThreadStatus = (id: string, status: 'open' | 'archived') =>
  portalFetch<{ ok: boolean; status: string }>(`/pm/messages/threads/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });

export const startThread = (participant: { id: string; name?: string; email?: string; kind?: 'owner' | 'tenant' }) =>
  portalFetch<{ thread: PmThread }>('/pm/messages/threads', {
    method: 'POST',
    body: JSON.stringify({
      participantId: participant.id,
      name: participant.name,
      email: participant.email,
      kind: participant.kind,
    }),
  });

export const fetchRecipients = (q = '') =>
  portalFetch<{ owners: PmRecipient[] }>(`/pm/messages/recipients${q ? `?q=${encodeURIComponent(q)}` : ''}`);

// ---------- polling ----------

/**
 * Re-run `fn` on an interval, but ONLY while somebody is actually there.
 *
 * WHY THIS EXISTS, AND WHY IT IS NOT JUST setInterval: the backend runs at
 * min-instances=0, so it costs nothing when idle — and a poll is precisely
 * the thing that stops it being idle. One phone left on this screen, awake
 * and untouched, pings often enough to hold a 2vCPU/2GiB container up all
 * day on its own. That is a real bill for nobody reading anything.
 *
 * So a tick is skipped when the app is in the background, and skipped again
 * once there has been no touch, key, scroll or focus for `idleAfterMs`. The
 * container is then free to scale back to zero. Nothing is lost by it: the
 * next interaction fetches immediately (so does coming back to the app), and
 * what appears is current as of that moment rather than as of a poll nobody
 * was watching.
 */
export function pollWhileActive(fn: () => void, intervalMs: number, idleAfterMs = 5 * 60 * 1000): () => void {
  let last = Date.now();

  const tick = () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - last > idleAfterMs) return; // nobody is there — let it sleep
    fn();
  };

  // Any sign of life restarts the clock, and catches up straight away if the
  // polling had already stopped.
  const onActivity = () => {
    const wasIdle = Date.now() - last > idleAfterMs;
    last = Date.now();
    if (wasIdle && document.visibilityState === 'visible') fn();
  };

  // Coming back to the app always refreshes — that is the moment someone is
  // most likely to be looking for a new message.
  const onVisibility = () => {
    if (document.visibilityState !== 'visible') return;
    last = Date.now();
    fn();
  };

  const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'focus'];
  const id = window.setInterval(tick, intervalMs);
  events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    window.clearInterval(id);
    events.forEach((e) => window.removeEventListener(e, onActivity));
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

// ---------- formatting ----------

// "09:42" today, "Tue 09:42" this week, "18 Sep" beyond it. A chat that
// stamps every bubble with a full date reads like a log file.
export function messageTime(iso?: string | null, locale = 'en-GB'): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1 && d.getDate() === new Date().getDate()) return time;
  if (days < 7) return `${d.toLocaleDateString(locale, { weekday: 'short' })} ${time}`;
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}
